const selectors = {
	container: '.numbers',
	grid: 'div.numbers__grid',
	column: 'div.numbers__col',
	row: 'div.numbers__row',
	number: 'span.numbers__number',
};

const classNames = {
	show: 'show',
	bento: 'numbers--bento',
};

const animation = {
	defaultDuration: 2,
	frameRate: 25,
	threshold: 0.5,
};

const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

const isBento = (container: HTMLElement) => container.classList.contains(classNames.bento);

const isAnimating = (container: HTMLElement) => container.dataset.animating === 'true';

const setAnimating = (container: HTMLElement, animating: boolean) => {
	container.dataset.animating = animating ? 'true' : 'false';
};

const getNumber = (value: string | null | undefined) => {
	const number = parseFloat(value ?? '');
	return isNaN(number) ? null : number;
};

const updateBentoSizes = (container: HTMLElement) => {
	if (!isBento(container)) {
		return;
	}

	const grid = container.querySelector(selectors.grid) as HTMLElement;
	if (!grid) {
		return;
	}

	grid.style.setProperty('--item-width', `${grid.clientWidth}px`);
	grid.style.setProperty('--height', `${grid.clientHeight}px`);
	grid.style.setProperty('--width', `${grid.scrollWidth}px`);

	grid.querySelectorAll(selectors.column).forEach((column) => {
		(column as HTMLElement).style.setProperty('--offset-left', `${(column as HTMLElement).offsetLeft * -1}px`);
	});
};

const showNumberItems = (container: HTMLElement) => {
	const itemSelector = isBento(container) ? selectors.row : selectors.column;
	container.querySelectorAll(itemSelector).forEach((item) => item.classList.add(classNames.show));
};

const getAnimationDuration = (numberEl: HTMLElement) => {
	const duration = parseFloat((numberEl.closest(selectors.grid) as HTMLElement)?.dataset.duration ?? '');
	return duration > 0 ? duration : animation.defaultDuration;
};

const getFormattedNumber = (value: number, preventDecimals = false) => {
	if (preventDecimals) {
		// Return rounded number if it's an integer
		return Math.round(value).toString();
	} else {
		// Return rounded number with 2 decimal places if it's a float
		return (Math.round(value * 100) / 100).toLocaleString(navigator.language);
	}
};

const writeNumberValue = (numberEl: HTMLElement, value: string) => {
	numberEl.innerText = value;

	if (numberEl.dataset.suffix) {
		numberEl.innerText += `${numberEl.dataset.suffix}`;
	}
};

const animateNumber = (numberEl: HTMLElement, startNumber: number, endNumber: number, preventDecimals: boolean) =>
	new Promise<void>((resolve) => {
		const diff = Math.abs(endNumber - startNumber);
		const duration = getAnimationDuration(numberEl);
		const totalFrames = Math.max(1, Math.round(duration * animation.frameRate));
		const intervalSpeed = 1000 / animation.frameRate;
		const step = diff / totalFrames;
		let frame = 1;

		const interval = setInterval(() => {
			const calculatedNumber = startNumber < endNumber ? startNumber + step * frame : startNumber - step * frame;
			writeNumberValue(numberEl, getFormattedNumber(calculatedNumber, preventDecimals));

			if (frame >= totalFrames) {
				clearInterval(interval);
				resolve();
			}

			frame++;
		}, intervalSpeed);
	});

const showNumber = (numberEl: HTMLElement) => {
	if (numberEl.dataset.textNumber) {
		writeNumberValue(numberEl, numberEl.dataset.textNumber);
		return null;
	}

	const startNumber = getNumber(numberEl.dataset.start);
	const endNumber = getNumber(numberEl.dataset.end);
	if (endNumber === null) {
		return null;
	}

	const endNumberIsInt = endNumber % 1 === 0;
	if (startNumber === null || reducedMotionQuery.matches) {
		writeNumberValue(numberEl, getFormattedNumber(endNumber, endNumberIsInt));
		return null;
	}

	return animateNumber(numberEl, startNumber, endNumber, endNumberIsInt);
};

const showAllNumbers = (container: HTMLElement) => {
	if (isAnimating(container)) {
		return;
	}

	setAnimating(container, true);
	showNumberItems(container);

	const animations = [...container.querySelectorAll<HTMLElement>(selectors.number)].map(showNumber).filter(Boolean);

	if (!animations.length) {
		setAnimating(container, false);
		return;
	}

	Promise.all(animations).then(() => setAnimating(container, false));
};

const onScreenEnter = (entries: IntersectionObserverEntry[]) => {
	entries.forEach((entry) => {
		if (entry.isIntersecting) {
			// The animation will trigger every time the element enters the viewport
			showAllNumbers(entry.target as HTMLElement);
		}
	});
};

const initNumbers = () => {
	const containers = document.querySelectorAll(selectors.container);
	if (!containers.length) {
		return;
	}

	const observer = new IntersectionObserver(onScreenEnter, {
		threshold: animation.threshold,
	});

	containers.forEach((container) => {
		updateBentoSizes(container as HTMLElement);
		observer.observe(container);
	});

	window.addEventListener('resize', () => {
		containers.forEach((container) => updateBentoSizes(container as HTMLElement));
	});
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initNumbers);
} else {
	initNumbers();
}
